import React, { useState, useEffect, useCallback } from 'react';
import { familiesAPI } from '../api/apiService';
import styles from './AdminFamiliesPage.module.css';

const AdminFamiliesPage = () => {
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(null); // Stores family to edit

  const [newFamily, setNewFamily] = useState({
    lastName: '',
    accountEmail: '',
    fatherName: '',
    fatherEmail: '',
    fatherPhone: '',
    motherName: '',
    motherEmail: '',
    motherPhone: '',
  });

  const fetchFamilies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await familiesAPI.list();
      setFamilies(result.families || []);
    } catch (err) {
      console.error("Error fetching families:", err);
      setError(err.message || "Failed to load families.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFamilies();
  }, [fetchFamilies]);

  const handleInputChange = (e, formType = 'new') => {
    const { name, value } = e.target;
    if (formType === 'edit' && showEditForm) {
      setShowEditForm(prev => ({ ...prev, [name]: value }));
    } else {
      setNewFamily(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddFamily = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await familiesAPI.create(newFamily);
      setShowAddForm(false);
      setNewFamily({ lastName: '', accountEmail: '', fatherName: '', fatherEmail: '', fatherPhone: '', motherName: '', motherEmail: '', motherPhone: '' });
      fetchFamilies(); // Refresh list
    } catch (err) {
      console.error("Error creating family:", err);
      setError(err.message || "Failed to create family.");
    }
  };

  const handleEditFamily = async (e) => {
    e.preventDefault();
    if (!showEditForm || !showEditForm.familyId) return;
    setError(null);
    try {
      const { familyId, createdAt, updatedAt, ...updateData } = showEditForm; // Exclude non-editable fields
      await familiesAPI.update(familyId, updateData);
      setShowEditForm(null);
      fetchFamilies(); // Refresh list
    } catch (err) {
      console.error("Error updating family:", err);
      setError(err.message || "Failed to update family.");
    }
  };
  
  const handleDeleteFamily = async (familyId) => {
    if (!window.confirm("Are you sure you want to delete this family? This will also unlink them from any donations.")) {
      return;
    }
    setError(null);
    try {
      await familiesAPI.delete(familyId);
      fetchFamilies(); // Refresh list
    } catch (err) {
      console.error("Error deleting family:", err);
      setError(err.message || "Failed to delete family.");
    }
  };

  const openEditForm = (family) => {
    setShowEditForm({...family}); // Populate form with family data
    setShowAddForm(false);
  };

  if (loading) return <p>Loading families...</p>;

  return (
    <div className={styles.familiesContainer}>
      <h2>Manage Families</h2>
      {error && <p className={styles.error}>{error}</p>}
      
      <button onClick={() => { setShowAddForm(!showAddForm); setShowEditForm(null); }} className={styles.addButton}>
        {showAddForm ? 'Cancel' : 'Add New Family'}
      </button>

      {(showAddForm || showEditForm) && (
        <form onSubmit={showEditForm ? handleEditFamily : handleAddFamily} className={styles.familyForm}>
          <h3>{showEditForm ? 'Edit Family' : 'Add New Family'}</h3>
          <div>
            <label>Last Name*:</label>
            <input type="text" name="lastName" value={showEditForm ? showEditForm.lastName : newFamily.lastName} onChange={(e) => handleInputChange(e, showEditForm ? 'edit' : 'new')} required />
          </div>
          <div>
            <label>Account Email*:</label>
            <input type="email" name="accountEmail" value={showEditForm ? showEditForm.accountEmail : newFamily.accountEmail} onChange={(e) => handleInputChange(e, showEditForm ? 'edit' : 'new')} required />
          </div>
          <div>
            <label>Father's Name:</label>
            <input type="text" name="fatherName" value={showEditForm ? showEditForm.fatherName : newFamily.fatherName} onChange={(e) => handleInputChange(e, showEditForm ? 'edit' : 'new')} />
          </div>
          <div>
            <label>Father's Email:</label>
            <input type="email" name="fatherEmail" value={showEditForm ? showEditForm.fatherEmail : newFamily.fatherEmail} onChange={(e) => handleInputChange(e, showEditForm ? 'edit' : 'new')} />
          </div>
          <div>
            <label>Father's Phone:</label>
            <input type="tel" name="fatherPhone" value={showEditForm ? showEditForm.fatherPhone : newFamily.fatherPhone} onChange={(e) => handleInputChange(e, showEditForm ? 'edit' : 'new')} />
          </div>
          <div>
            <label>Mother's Name:</label>
            <input type="text" name="motherName" value={showEditForm ? showEditForm.motherName : newFamily.motherName} onChange={(e) => handleInputChange(e, showEditForm ? 'edit' : 'new')} />
          </div>
          <div>
            <label>Mother's Email:</label>
            <input type="email" name="motherEmail" value={showEditForm ? showEditForm.motherEmail : newFamily.motherEmail} onChange={(e) => handleInputChange(e, showEditForm ? 'edit' : 'new')} />
          </div>
          <div>
            <label>Mother's Phone:</label>
            <input type="tel" name="motherPhone" value={showEditForm ? showEditForm.motherPhone : newFamily.motherPhone} onChange={(e) => handleInputChange(e, showEditForm ? 'edit' : 'new')} />
          </div>
          <button type="submit">{showEditForm ? 'Save Changes' : 'Create Family'}</button>
          {showEditForm && <button type="button" onClick={() => setShowEditForm(null)}>Cancel Edit</button>}
        </form>
      )}

      <table className={styles.familiesTable}>
        <thead>
          <tr>
            <th>Last Name</th>
            <th>Account Email</th>
            <th>Father's Name</th>
            <th>Mother's Name</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {families.map(family => (
            <tr key={family.familyId}>
              <td>{family.lastName}</td>
              <td>{family.accountEmail}</td>
              <td>{family.fatherName || '-'}</td>
              <td>{family.motherName || '-'}</td>
              <td>
                <button onClick={() => openEditForm(family)} className={styles.editButton}>Edit</button>
                <button onClick={() => handleDeleteFamily(family.familyId)} className={styles.deleteButton}>Delete</button>
              </td>
            </tr>
          ))}
          {families.length === 0 && !loading && (
            <tr>
              <td colSpan="5">No families found.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AdminFamiliesPage;
